import { useReplicache, useSubscribe } from "../replicache/context";
import { ITEM_PREFIX, type Group, type Item } from "../replicache/keys";

// Cross-group "what needs doing now" view: overdue, due today, and starred
// tasks in one list, regardless of which group they live in.  Rows complete
// in place or jump to the task in its group (same reveal path as search).

type Bucket = "overdue" | "today" | "starred";

function todayISO(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function TodayView({
  groups,
  onJumpToItem,
}: {
  groups: Group[];
  onJumpToItem: (itemId: string, groupId: string) => void;
}) {
  const rep = useReplicache();
  const items = useSubscribe<Item[]>(
    rep,
    async (tx) => {
      const out: Item[] = [];
      for await (const value of tx.scan({ prefix: ITEM_PREFIX }).values()) {
        const it = value as Item;
        if (it.type !== "task" || it.isComplete) continue;
        if (it.dueDate || it.isFocused) out.push(it);
      }
      return out;
    },
    [],
  );

  const today = todayISO();
  const groupName = new Map(groups.map((g) => [g.id, g.name]));

  const buckets: Record<Bucket, Item[]> = {
    overdue: [],
    today: [],
    starred: [],
  };
  for (const it of items) {
    const due = it.dueDate?.slice(0, 10) ?? null;
    if (due && due < today) buckets.overdue.push(it);
    else if (due === today) buckets.today.push(it);
    else if (it.isFocused) buckets.starred.push(it);
  }
  buckets.overdue.sort((a, b) => (a.dueDate! < b.dueDate! ? -1 : 1));
  buckets.today.sort((a, b) => (a.sortKey < b.sortKey ? -1 : 1));
  buckets.starred.sort((a, b) => (a.sortKey < b.sortKey ? -1 : 1));

  const total =
    buckets.overdue.length + buckets.today.length + buckets.starred.length;

  const sections: { key: Bucket; title: string; accent: string }[] = [
    { key: "overdue", title: "Overdue", accent: "text-red-600 dark:text-red-400" },
    { key: "today", title: "Due today", accent: "text-slate-800 dark:text-slate-100" },
    { key: "starred", title: "Starred", accent: "text-amber-600 dark:text-amber-400" },
  ];

  return (
    <section className="min-w-0 flex-1 overflow-y-auto">
      <header className="border-b border-slate-200 px-4 py-3 sm:px-6 sm:py-4 dark:border-slate-700">
        <h2 className="text-xl font-semibold tracking-tight">Today</h2>
        <p className="text-xs text-slate-400 dark:text-slate-500">
          Overdue, due today, and starred tasks across every group.
        </p>
      </header>
      {total === 0 ? (
        <p className="p-8 text-center text-sm text-slate-500 dark:text-slate-400">
          Nothing due and nothing starred — all clear. 🎉
        </p>
      ) : (
        <div className="space-y-5 p-4 sm:p-6">
          {sections.map(({ key, title, accent }) =>
            buckets[key].length === 0 ? null : (
              <section key={key}>
                <h3
                  className={`mb-1.5 text-xs font-semibold uppercase tracking-wide ${accent}`}
                >
                  {title} · {buckets[key].length}
                </h3>
                <ul className="space-y-0.5">
                  {buckets[key].map((it) => (
                    <TodayRow
                      key={it.id}
                      item={it}
                      group={groupName.get(it.groupId) ?? ""}
                      showDate={key === "overdue"}
                      onJump={() => onJumpToItem(it.id, it.groupId)}
                    />
                  ))}
                </ul>
              </section>
            ),
          )}
        </div>
      )}
    </section>
  );
}

function TodayRow({
  item,
  group,
  showDate,
  onJump,
}: {
  item: Item;
  group: string;
  showDate: boolean;
  onJump: () => void;
}) {
  const rep = useReplicache();
  return (
    <li className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-slate-50 dark:hover:bg-slate-800">
      <input
        type="checkbox"
        checked={false}
        onChange={() => void rep?.mutate.completeItem({ id: item.id })}
        aria-label={`Complete "${item.title}"`}
        className="h-4 w-4 shrink-0 accent-slate-700"
      />
      <button
        type="button"
        onClick={onJump}
        className="flex min-w-0 flex-1 items-baseline gap-2 text-left"
      >
        <span className="truncate text-sm text-slate-800 dark:text-slate-100">
          {item.isFocused && (
            <span aria-hidden className="mr-1 text-amber-500">
              ★
            </span>
          )}
          {item.title || "(untitled)"}
        </span>
        {showDate && item.dueDate && (
          <span className="shrink-0 text-xs text-red-500">
            {item.dueDate.slice(5, 10).replace("-", "/")}
          </span>
        )}
        <span className="ml-auto shrink-0 pl-2 text-xs text-slate-400 dark:text-slate-500">
          {group}
        </span>
      </button>
    </li>
  );
}
